// EarningDetails.jsx
import React, { useState, useEffect } from "react";
import {
  FaChevronRight,
  FaChevronLeft,
  FaChevronDown,
  FaCalendarAlt,
  FaCalendarDay,
  FaArrowLeft,
  FaArrowRight,
  FaSpinner,
} from "react-icons/fa";

const EarningDetails = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [showYearSelector, setShowYearSelector] = useState(false);
  const [showMonthSelector, setShowMonthSelector] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initial data state
  const [earningsData, setEarningsData] = useState({
    selectedDate: "06 January",
    amounts: {
      selectedDate: "0.0",
      yesterday: "0.0",
      dailyAverage: "0.0",
    },
    categories: {
      "Private Call": "0.0",
      "Public Stream": "0.0",
      "Premium Stream": "0.0",
      Messages: "0.0",
      Bonuses: "0.0",
    },
  });

  // Month names
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Days of week
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Function to format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Function to get yesterday's date
  const getYesterdayDate = (date) => {
    const yesterday = new Date(date);
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  };

  // Function to fetch data from API
  const fetchEarningsData = async (date) => {
    setLoading(true);
    setError(null);

    try {
      // Format dates for API
      const selectedDateStr = formatDateForAPI(date);
      const yesterdayDate = getYesterdayDate(date);
      const yesterdayStr = formatDateForAPI(yesterdayDate);

      // === REPLACE THIS WITH YOUR ACTUAL API CALL ===
      // Example API endpoint:
      // const apiUrl = `https://your-api.com/earnings?selectedDate=${selectedDateStr}&yesterday=${yesterdayStr}`;

      // For now, simulate API delay and return dummy data
      await new Promise((resolve) => setTimeout(resolve, 500));

      const apiData = {
        date: selectedDateStr,
        selectedDate: `${date.getDate()} ${months[date.getMonth()]}`,
        amounts: {
          selectedDate: (Math.random() * 200).toFixed(1),
          yesterday: (Math.random() * 150).toFixed(1),
          dailyAverage: (Math.random() * 100).toFixed(1),
        },
        categories: {
          "Private Call": (Math.random() * 80).toFixed(1),
          "Public Stream": (Math.random() * 60).toFixed(1),
          "Premium Stream": (Math.random() * 40).toFixed(1),
          Messages: (Math.random() * 30).toFixed(1),
          Bonuses: (Math.random() * 20).toFixed(1),
        },
      };

      // Update state with API data
      setEarningsData({
        selectedDate: apiData.selectedDate,
        amounts: apiData.amounts,
        categories: apiData.categories,
      });
    } catch (err) {
      setError("Failed to fetch earnings data. Please try again.");
      console.error("API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when component mounts and when date changes
  useEffect(() => {
    fetchEarningsData(selectedDate);
  }, [selectedDate]);

  // Generate calendar data
  const generateCalendar = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];

    // Previous month days
    for (let i = startingDay - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({
        date,
        isCurrentMonth: false,
        isSelected: false,
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const isToday = date.toDateString() === new Date().toDateString();
      const isSelected = date.toDateString() === selectedDate.toDateString();

      days.push({
        date,
        isCurrentMonth: true,
        isToday,
        isSelected,
      });
    }

    return days;
  };

  const calendarDays = generateCalendar();

  // Generate years for selector (from 2020 to current year + 2)
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 2019 + 2 },
    (_, i) => 2020 + i
  );

  // Handle date selection
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setShowCalendar(false);
  };

  // Calendar navigation
  const handlePrevMonth = () => {
    setSelectedDate(
      new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setSelectedDate(
      new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1)
    );
  };

  const handleMonthSelect = (monthIndex) => {
    setSelectedDate(new Date(selectedDate.getFullYear(), monthIndex, 1));
    setShowMonthSelector(false);
  };

  const handleYearSelect = (year) => {
    setSelectedDate(new Date(year, selectedDate.getMonth(), 1));
    setShowYearSelector(false);
  };

  // Category click handler
  const handleCategoryClick = (category) => {
    setSelectedCategory(selectedCategory === category ? null : category);
    // Optional: Fetch detailed category data here
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `$${parseFloat(amount).toFixed(1)}`;
  };

  // Category icons
  const categoryIcons = {
    "Private Call": "📞",
    "Public Stream": "📺",
    "Premium Stream": "⭐",
    Messages: "💬",
    Bonuses: "🎁",
  };

  return (
    <div className="min-h-screen bg-black text-white p-4 pb-20">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">EARNINGS</h1>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-300">Calendar</h2>
          <button
            onClick={() => setShowCalendar(!showCalendar)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 rounded-lg hover:bg-gray-800 transition"
            disabled={loading}
          >
            <FaCalendarAlt className="text-lg" />
            <span>{showCalendar ? "Hide" : "Show"} Calendar</span>
          </button>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center">
          <div className="bg-gray-900 p-8 rounded-xl flex flex-col items-center">
            <FaSpinner className="text-4xl text-purple-500 animate-spin mb-4" />
            <p className="text-lg">Fetching earnings data...</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-900 bg-opacity-30 border border-red-700 rounded-xl">
          <div className="flex justify-between items-center">
            <p className="text-red-300">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-300"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Calendar Modal */}
      {showCalendar && (
        <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-md p-6">
            {/* Year Selector */}
            {showYearSelector && (
              <div className="absolute inset-0 bg-gray-900 rounded-2xl p-6 z-10">
                <div className="flex justify-between items-center mb-6">
                  <button
                    onClick={() => setShowYearSelector(false)}
                    className="p-2 hover:bg-gray-800 rounded-lg"
                  >
                    <FaChevronLeft className="text-xl" />
                  </button>
                  <h3 className="text-xl font-bold">Select Year</h3>
                  <div className="w-10"></div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {years.map((year) => (
                    <button
                      key={year}
                      onClick={() => handleYearSelect(year)}
                      className={`
                        py-4 rounded-lg text-center transition-all
                        ${
                          selectedDate.getFullYear() === year
                            ? "bg-purple-600 text-white"
                            : "bg-gray-800 hover:bg-gray-700"
                        }
                      `}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Month Selector */}
            {showMonthSelector && (
              <div className="absolute inset-0 bg-gray-900 rounded-2xl p-6 z-10">
                <div className="flex justify-between items-center mb-6">
                  <button
                    onClick={() => setShowMonthSelector(false)}
                    className="p-2 hover:bg-gray-800 rounded-lg"
                  >
                    <FaChevronLeft className="text-xl" />
                  </button>
                  <h3 className="text-xl font-bold">Select Month</h3>
                  <div className="w-10"></div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {months.map((month, index) => (
                    <button
                      key={month}
                      onClick={() => handleMonthSelect(index)}
                      className={`
                        py-4 rounded-lg text-center transition-all
                        ${
                          selectedDate.getMonth() === index
                            ? "bg-purple-600 text-white"
                            : "bg-gray-800 hover:bg-gray-700"
                        }
                      `}
                    >
                      {month}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Main Calendar View */}
            {!showYearSelector && !showMonthSelector && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={handlePrevMonth}
                    className="p-2 hover:bg-gray-800 rounded-lg"
                  >
                    <FaChevronLeft className="text-xl" />
                  </button>

                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setShowMonthSelector(true)}
                      className="px-4 py-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition text-lg font-medium"
                    >
                      {months[selectedDate.getMonth()]}
                    </button>

                    <button
                      onClick={() => setShowYearSelector(true)}
                      className="px-4 py-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition text-lg font-medium"
                    >
                      {selectedDate.getFullYear()}
                    </button>
                  </div>

                  <button
                    onClick={handleNextMonth}
                    className="p-2 hover:bg-gray-800 rounded-lg"
                  >
                    <FaChevronRight className="text-xl" />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-1 mb-6">
                  {daysOfWeek.map((day) => (
                    <div
                      key={day}
                      className="text-center text-gray-400 text-sm py-2"
                    >
                      {day}
                    </div>
                  ))}

                  {calendarDays.map((day, index) => (
                    <button
                      key={index}
                      onClick={() => handleDateSelect(day.date)}
                      disabled={loading}
                      className={`
                        p-3 rounded-lg text-center transition-all
                        ${!day.isCurrentMonth ? "text-gray-600" : ""}
                        ${day.isToday ? "bg-blue-900 text-blue-100" : ""}
                        ${
                          day.isSelected
                            ? "bg-purple-600 text-white"
                            : "hover:bg-gray-800"
                        }
                        ${loading ? "opacity-50 cursor-not-allowed" : ""}
                      `}
                    >
                      <div className="text-lg font-medium">
                        {day.date.getDate()}
                      </div>
                    </button>
                  ))}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleDateSelect(new Date())}
                    className="flex-1 py-3 bg-blue-600 rounded-lg font-medium hover:bg-blue-700 transition flex items-center justify-center gap-2"
                    disabled={loading}
                  >
                    <FaCalendarDay />
                    Today
                  </button>
                  <button
                    onClick={() => setShowCalendar(false)}
                    className="flex-1 py-3 bg-gray-800 rounded-lg font-medium hover:bg-gray-700 transition"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Date Display */}
      <div className="mb-8 p-4 bg-gray-900 rounded-xl">
        <div className="text-center mb-4">
          <div className="text-2xl font-bold text-gray-300 mb-1">
            {earningsData.selectedDate}
          </div>
          <div className="text-sm text-gray-400">
            {selectedDate.toLocaleDateString("en-US", { weekday: "long" })}
          </div>
        </div>

        {/* Amounts Table */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center">
            <div className="text-gray-400 text-sm mb-1">Selected Date</div>
            <div className="text-2xl font-bold">
              {loading
                ? "..."
                : formatCurrency(earningsData.amounts.selectedDate)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 text-sm mb-1">Yesterday</div>
            <div className="text-2xl font-bold">
              {loading ? "..." : formatCurrency(earningsData.amounts.yesterday)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 text-sm mb-1">Daily Average</div>
            <div className="text-2xl font-bold">
              {loading
                ? "..."
                : formatCurrency(earningsData.amounts.dailyAverage)}
            </div>
          </div>
        </div>

        {/* Selected Date Info */}
        <div className="text-center text-sm text-gray-400">
          Data for {earningsData.selectedDate}
          {loading && " (Loading...)"}
        </div>
      </div>

      {/* Categories Section */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-300">Matches</h3>

        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-gray-900 rounded-xl p-4 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-800 rounded-full"></div>
                    <div className="h-6 w-24 bg-gray-800 rounded"></div>
                  </div>
                  <div className="h-6 w-16 bg-gray-800 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {Object.entries(earningsData.categories).map(
              ([category, amount]) => (
                <div
                  key={category}
                  onClick={() => handleCategoryClick(category)}
                  className={`
                  bg-gray-900 rounded-xl p-4 transition-all cursor-pointer
                  ${
                    selectedCategory === category
                      ? "ring-2 ring-purple-500"
                      : "hover:bg-gray-800"
                  }
                `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{categoryIcons[category]}</span>
                      <span className="font-medium">{category}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-bold">
                        {formatCurrency(amount)}
                      </span>
                      <FaChevronRight className="text-gray-400" />
                    </div>
                  </div>

                  {/* Expanded Category Details */}
                  {selectedCategory === category && (
                    <div className="mt-4 pt-4 border-t border-gray-800">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-gray-400">Today</div>
                          <div className="font-medium">
                            {formatCurrency(amount)}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-400">Yesterday</div>
                          <div className="font-medium">
                            {formatCurrency(
                              (parseFloat(amount) * 0.8).toFixed(1)
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-400">This Week</div>
                          <div className="font-medium">
                            {formatCurrency(
                              (parseFloat(amount) * 5).toFixed(1)
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-400">This Month</div>
                          <div className="font-medium">
                            {formatCurrency(
                              (parseFloat(amount) * 20).toFixed(1)
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 flex gap-2">
                        <button className="flex-1 py-2 px-4 bg-purple-600 rounded-lg text-sm hover:bg-purple-700 transition">
                          View Details
                        </button>
                        <button className="flex-1 py-2 px-4 bg-gray-800 rounded-lg text-sm hover:bg-gray-700 transition">
                          <FaChevronDown className="inline mr-1" />
                          More
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Quick Date Selector */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 p-4">
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => {
                const yesterday = new Date(selectedDate);
                yesterday.setDate(yesterday.getDate() - 1);
                handleDateSelect(yesterday);
              }}
              disabled={loading}
              className="flex-1 py-3 bg-gray-800 rounded-lg text-sm font-medium hover:bg-gray-700 transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaArrowLeft />
              Yesterday
            </button>

            <button
              onClick={() => setShowCalendar(true)}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 rounded-lg font-medium hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaCalendarAlt />
              Pick Date
            </button>

            <button
              onClick={() => {
                const tomorrow = new Date(selectedDate);
                tomorrow.setDate(tomorrow.getDate() + 1);
                handleDateSelect(tomorrow);
              }}
              disabled={loading}
              className="flex-1 py-3 bg-gray-800 rounded-lg text-sm font-medium hover:bg-gray-700 transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Tomorrow
              <FaArrowRight />
            </button>
          </div>
        </div>
      </div>

      {/* Selected Date Indicator */}
      <div className="mt-8 p-4 bg-gray-900 rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-gray-400 text-sm">Currently Viewing</div>
            <div className="text-lg font-bold">{earningsData.selectedDate}</div>
            <div className="text-xs text-gray-500 mt-1">
              Last updated: {new Date().toLocaleTimeString()}
            </div>
          </div>
          <button
            onClick={() => handleDateSelect(new Date())}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 rounded-lg font-medium hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaCalendarDay />
            Back to Today
          </button>
        </div>
      </div>
    </div>
  );
};

export default EarningDetails;
